import { memo } from 'react';

import NoRunsBackgroundSVG from '@/assets/no-runs-background.svg?react';
import ReviewCompletedIcon from '@/assets/review-completed-icon.svg?react';
import { Flex, Heading, Icon, Text } from '@/components/core';
import { NoFilesLogo } from '@/components/logos';

import { UploadDocumentsDropzone } from './UploadDocumentsDropzone.tsx';

export type AgentRunsEmptyProps = {
  agentId?: string;
  location: 'review' | 'exported' | 'archived' | 'review-completed';
  showDropzone?: boolean;
};

const IAgentRunsEmpty = ({
  agentId,
  location,
  showDropzone,
  children,
}: React.PropsWithChildren<AgentRunsEmptyProps>) => {
  const headers =
    {
      review: 'No files to review',
      exported: 'No exported files yet',
      archived: 'No archived files yet',
      'review-completed': 'No more files to review',
    }[location] ?? 'No files available';

  const message =
    {
      review: 'There are no files flagged for manual review.',
      exported: 'You do not have any exported files yet.',
      archived: 'You do not have any archived files yet.',
      'review-completed': 'You have completed all files for review.',
    }[location] ?? 'There are no files here.';

  const icon = {
    review: undefined,
    exported: undefined,
    archived: undefined,
    'review-completed': <Icon SVGComponent={ReviewCompletedIcon} className="size-20" />,
  }[location] ?? <NoFilesLogo variant="field-type" size="xl" />;

  return showDropzone ? (
    <UploadDocumentsDropzone
      className="border-primary-100 m-1 flex h-[20rem] flex-col items-center justify-center rounded-lg border-dashed bg-white"
      agentId={agentId}
    />
  ) : (
    <Flex className="h-[60%] w-full items-center justify-center">
      <NoRunsBackgroundSVG className="absolute" />

      <Flex col className="gap-md z-1 translate-y-[30%] items-center">
        {icon}
        <Heading>{headers}</Heading>
        <Text>{message}</Text>
        {children}
      </Flex>
    </Flex>
  );
};

export const AgentRunsEmpty = memo(IAgentRunsEmpty) as typeof IAgentRunsEmpty;
